import { useState } from "react";

import { CheckEligibilityResponseProto_EligibilityReason } from "@noom/noom-contracts/noom_contracts/backend/partner/service/partner_eligibility_service";
import { Api } from "@noom/noomscape";

import { Sponsor } from "@/api/partner/getSponsors";
import { ErrorState, ProgramOffering } from "@/constants";
import { useAppContext } from "@/contexts";
import {
  EligibilityCandidateMatch,
  EligibilityInfo,
  EligibilityReason,
  EligibilityResponse,
  EligibilityVerificationProperties,
  Partner,
} from "@/models";
import { trackDatadogAction } from "@/utils/datadog";
import {
  buildEligibilityInfo,
  findMatchingCandidate,
  isMultipleMatchWithCandidates,
} from "@/utils/eligibility";
import { isCmsAccessEnabled } from "@/utils/partners";
import { captureException } from "@/utils/sentry";

import { useGoto } from "./useGoto";
import { QueryParamsWithoutPii, useQueryParams } from "./useQueryParams";
import { useTrackEvent } from "./useTrackEvent";

export type EligibilityResult = {
  candidateMatches: EligibilityCandidateMatch[];
  eligibilityInfo: EligibilityInfo;
  eligibilityReason: CheckEligibilityResponseProto_EligibilityReason;
  isEligible: boolean;
  participantEligibilityId: string | undefined;
  participantPartnerId: number | undefined;
  upid: string | undefined;
};

export const ELIGIBILITY_LOOKUP_ACTION = "enrollment.eligibility_lookup";

type EligibilityLookupOutcome =
  | "success"
  | "existing_account"
  | "not_found"
  | "error";

/**
 * Buckets an eligibility response into a telemetry outcome.
 *
 *   `success`           eligible, or MULTIPLE_MATCHES with candidates (the UI
 *                       resolves it via disambiguation)
 *   `existing_account`  member already has a Noom account; a normal outcome that
 *                       the UI routes to an "account exists" page, not a failure.
 *                       Matches the landing page's own check (`!eligible && accessCode`)
 *                       as well as the explicit EXISTING_ACCOUNT reason.
 *   `not_found`         well-formed response, member not eligible. Includes
 *                       MULTIPLE_MATCHES with no candidates, which the UI routes
 *                       to an error page (`reason` still says MULTIPLE_MATCHES).
 *   `error`             API_ERROR payload
 *
 * The failure monitors alert on `error` and `not_found` only.
 */
const classifyEligibilityOutcome = (
  isEligible: boolean,
  eligibilityReason: CheckEligibilityResponseProto_EligibilityReason,
  accessCode?: string,
  candidateMatches?: EligibilityCandidateMatch[],
): EligibilityLookupOutcome => {
  if (eligibilityReason === EligibilityReason.API_ERROR) return "error";
  if (
    isEligible ||
    isMultipleMatchWithCandidates(eligibilityReason, candidateMatches)
  ) {
    return "success";
  }
  if (eligibilityReason === EligibilityReason.EXISTING_ACCOUNT || accessCode) {
    return "existing_account";
  }
  return "not_found";
};

export const useEligibility = () => {
  const {
    enrollmentInfo,
    partnerInfo,
    setEligibilityInfo,
    setQualifiedPrograms,
    setCandidateMatches,
  } = useAppContext();
  const goto = useGoto();
  const queryParams = useQueryParams();
  const [hasEligibilityError, setHasEligibilityError] =
    useState<boolean>(false);
  const { trackAnonEvent, trackIdentifiedEvent } = useTrackEvent();

  const applyEligibilityResponse = (
    response: EligibilityResponse,
  ): {
    eligibilityInfo: EligibilityInfo;
    eligiblePrograms: ProgramOffering[];
  } => {
    const { eligibilityInfo, eligiblePrograms } = buildEligibilityInfo(
      response,
      enrollmentInfo,
    );
    setEligibilityInfo({ ...eligibilityInfo });
    setQualifiedPrograms(eligiblePrograms);
    return { eligibilityInfo, eligiblePrograms };
  };

  const redirectToParticipantPartner = async (
    eligibilityId: string | undefined,
    participantPartnerId: number | undefined,
    upid: string | undefined,
  ) => {
    if (!(!!eligibilityId && !!participantPartnerId && !!upid)) {
      captureException(
        Error(
          "Attempted to redirect for shared eligibility partner(s) but required redirect params are missing",
        ),
      );
      goto.error(ErrorState.ELIGIBILITY_CHECK_API_ERROR);
      return;
    }
    const partner: Partner = await Api.call(
      "partner.getPartnerByUpid",
      Api.api.partner.getPartnerByUpid,
      { upid },
    );

    const newParams: QueryParamsWithoutPii = {
      ...queryParams,
      eligibilityId,
      sharedEligibility: "true",
      upid,
    };
    const qs = new URLSearchParams(
      Object.entries(newParams).filter(([, v]) => v !== undefined),
    );

    window.location.href = `${window.location.origin}/employer/${partner.slug}?${qs.toString()}`;
  };

  const redirectToCandidateMatch = async (match: EligibilityCandidateMatch) =>
    redirectToParticipantPartner(
      match.eligibilityId,
      match.partnerId,
      match.upid,
    );

  const checkEligibility = async (
    eligibilityVerificationProperties: EligibilityVerificationProperties,
    partnerId: number,
    leadSource?: string,
  ): Promise<EligibilityResult | null> => {
    setHasEligibilityError(false);
    setCandidateMatches([]);
    let response: EligibilityResponse | undefined;
    try {
      response = await Api.call(
        "partner.checkEligibility",
        Api.api.partner.checkEligibility,
        { eligibilityVerificationProperties, leadSource, partnerId },
      );
    } catch (error) {
      captureException(error);
      response = undefined;
    }

    if (!response) {
      trackDatadogAction(ELIGIBILITY_LOOKUP_ACTION, {
        source: "triage_form",
        outcome: "error",
        reason: EligibilityReason.API_ERROR,
        partnerId,
      });
      goto.error(ErrorState.ELIGIBILITY_CHECK_API_ERROR);
      return null;
    }

    const {
      accessCode,
      eligible: isEligible,
      eligibilityId: participantEligibilityId,
      eligibilityReason,
      partnerId: participantPartnerId,
      upid,
      candidateMatches = [],
    } = response;

    trackDatadogAction(ELIGIBILITY_LOOKUP_ACTION, {
      source: "triage_form",
      outcome: classifyEligibilityOutcome(
        isEligible,
        eligibilityReason,
        accessCode,
        candidateMatches as EligibilityCandidateMatch[],
      ),
      reason: eligibilityReason,
      partnerId,
    });

    const { eligibilityInfo } = applyEligibilityResponse(response);
    const normalizedCandidateMatches =
      candidateMatches as EligibilityCandidateMatch[];

    const eventParams = {
      eligibleForEnrollment: isEligible,
      eligibilityStatus: eligibilityReason,
    };

    // ACCESS has no upfront eligibility check, so the result is always
    // ELIGIBLE and these events would carry no information.
    if (!isCmsAccessEnabled(partnerInfo)) {
      trackAnonEvent("b2bEnrollmentAnonEligibilityChecked", eventParams);
      trackIdentifiedEvent(
        "b2bEnrollmentIdentifiedEligibilityChecked",
        eventParams,
      );
    }

    if (isEligible === false) {
      if (eligibilityReason === EligibilityReason.EXISTING_ACCOUNT) {
        goto.error(ErrorState.ELIGIBILITY_CHECK_EXISTING_ACCOUNT);
      } else if (
        eligibilityReason === EligibilityReason.PRIOR_TO_EFFECTIVE_DATE
      ) {
        goto.error(ErrorState.PRIOR_TO_EFFECTIVE_DATE);
      } else if (eligibilityReason === EligibilityReason.API_ERROR) {
        goto.error(ErrorState.ELIGIBILITY_CHECK_API_ERROR);
      } else if (eligibilityReason === EligibilityReason.MULTIPLE_MATCHES) {
        if (
          isMultipleMatchWithCandidates(
            eligibilityReason,
            normalizedCandidateMatches,
          )
        ) {
          goto.eligibilityDisambiguation(normalizedCandidateMatches);
        } else {
          goto.error(ErrorState.ELIGIBILITY_CHECK_MULTIPLE_MATCHES);
        }
      } else {
        setHasEligibilityError(true);
      }
    }

    return {
      candidateMatches: normalizedCandidateMatches,
      eligibilityInfo,
      eligibilityReason,
      isEligible,
      participantEligibilityId,
      participantPartnerId,
      upid,
    };
  };

  const fetchEligibilityInfo = async (
    eligibilityId: string,
    partnerId: number,
  ): Promise<EligibilityResponse> => {
    let response: EligibilityResponse;
    try {
      response = await Api.call(
        "partner.checkEligibility",
        Api.api.partner.checkEligibility,
        { eligibilityVerificationProperties: { eligibilityId }, partnerId },
      );
    } catch (error) {
      trackDatadogAction(ELIGIBILITY_LOOKUP_ACTION, {
        source: "eligibility_id",
        outcome: "error",
        reason: EligibilityReason.API_ERROR,
        partnerId,
      });
      throw error;
    }
    trackDatadogAction(ELIGIBILITY_LOOKUP_ACTION, {
      source: "eligibility_id",
      outcome: classifyEligibilityOutcome(
        response.eligible,
        response.eligibilityReason,
        response.accessCode,
        response.candidateMatches as EligibilityCandidateMatch[] | undefined,
      ),
      reason: response.eligibilityReason,
      partnerId,
    });
    return response;
  };

  return {
    applyEligibilityResponse,
    checkEligibility,
    fetchEligibilityInfo,
    hasEligibilityError,
    redirectToCandidateMatch,
    redirectToParticipantPartner,
    setHasEligibilityError,
  };
};

export const useHandleSponsorSelect = (
  candidateMatches: EligibilityCandidateMatch[],
  redirectToCandidateMatch: (match: EligibilityCandidateMatch) => Promise<void>,
) => {
  const goto = useGoto();
  const { trackAnonEvent } = useTrackEvent();
  const [isLoading, setIsLoading] = useState(false);

  const handleSponsorSelect = async (sponsor: Sponsor) => {
    const candidate = findMatchingCandidate(sponsor, candidateMatches);
    if (!candidate) {
      trackAnonEvent("b2bEnrollmentAnonButtonClicked", {
        buttonName: "sponsorNotInCandidates",
        pageId: "eligibilityDisambiguation",
      });
      goto.error(ErrorState.ELIGIBILITY_CHECK_MULTIPLE_MATCHES);
      return;
    }
    trackAnonEvent("b2bEnrollmentAnonButtonClicked", {
      buttonName: "sponsorSelected",
      pageId: "eligibilityDisambiguation",
    });
    try {
      setIsLoading(true);
      await redirectToCandidateMatch(candidate);
    } catch (error) {
      captureException(error);
      goto.error(ErrorState.ELIGIBILITY_CHECK_API_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  return { handleSponsorSelect, isLoading };
};
